import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IoClose } from "react-icons/io5";
import { FiUpload } from "react-icons/fi";
import { toast } from "react-toastify";
import moment from "moment";
import CInput from "../../../../../components/CInput";
import CAutocomplete from "../../../../../components/CAutocomplete";
import {
  getIssueLogDetail,
  updateIssueLog,
  resolveIssueLog,
} from "../../../../../services/issueLogService";
import {
  ISSUE_STATUS,
  getStatusStyles,
  getPriorityStyles,
  getSourceTypeLabel,
  formatEnumForDisplay,
  getMarkupFromMetadata,
} from "../../ProjectLogIssues/constants";
import ImageLightbox from "../../ProjectLogIssues/ImageLightbox";

const STATUS_OPTIONS = [
  { value: ISSUE_STATUS.OPEN, label: "Open" },
  { value: ISSUE_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: ISSUE_STATUS.DONE, label: "Done" },
  { value: ISSUE_STATUS.RESOLVED, label: "Resolved" },
];

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  textTransform: "capitalize",
  width: "fit-content"
};

export default function IssueDetailModal({ issueId, onClose, onChanged }) {
  const userData = useSelector((s) => s.auth?.userData);
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [comments, setComments] = useState("");
  const [resolveFile, setResolveFile] = useState(null);
  const [resolveFilePreview, setResolveFilePreview] = useState(null);
  const [issueLightboxOpen, setIssueLightboxOpen] = useState(false);
  const [resolvedLightboxOpen, setResolvedLightboxOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!issueId) return;
      setLoading(true);
      try {
        const res = await getIssueLogDetail(issueId);
        const data = res?.data ?? res;
        if (!cancelled) {
          setIssue(data);
          const current = (data?.status || "").toUpperCase().replace(/\s+/g, "_");
          setSelectedStatus(
            STATUS_OPTIONS.find((o) => o.value === current) || null
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Could not load issue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  const handleResolveFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (resolveFilePreview) URL.revokeObjectURL(resolveFilePreview);
    setResolveFile(f);
    setResolveFilePreview(URL.createObjectURL(f));
  };

  const removeResolveFile = () => {
    if (resolveFilePreview) URL.revokeObjectURL(resolveFilePreview);
    setResolveFile(null);
    setResolveFilePreview(null);
  };

  const parseResolvedMeta = () => {
    if (!issue?.resolved_metadata) return null;
    try {
      return typeof issue.resolved_metadata === "string"
        ? JSON.parse(issue.resolved_metadata)
        : issue.resolved_metadata;
    } catch {
      return null;
    }
  };

  const isAlreadyResolved =
    !!(
      issue &&
      (issue.status || "").toUpperCase().replace(/\s+/g, "_") ===
        ISSUE_STATUS.RESOLVED
    );
  const isSelectingResolved =
    selectedStatus?.value === ISSUE_STATUS.RESOLVED && !isAlreadyResolved;
  const resolveValid =
    !isSelectingResolved || (comments.trim() && resolveFile);

  const handleSave = async () => {
    if (!issue || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }
    if (isSelectingResolved && (!comments.trim() || !resolveFile)) {
      toast.error("Comments and image are required when resolving");
      return;
    }
    setSaving(true);
    try {
      const body = { ...issue, status: selectedStatus.value };
      await updateIssueLog(issue.id, body);

      if (isSelectingResolved) {
        const resolvedBy = userData?.id;
        if (!resolvedBy) {
          toast.error("User not found. Cannot record who resolved.");
          setSaving(false);
          return;
        }
        await resolveIssueLog(issue.id, {
          comments: comments.trim(),
          resolvedBy,
          image: resolveFile,
        });
      }
      toast.success(isSelectingResolved ? "Issue resolved" : "Status updated");
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Could not update issue");
    } finally {
      setSaving(false);
    }
  };

  const resolvedMeta = parseResolvedMeta();
  const markup = issue ? getMarkupFromMetadata(issue.metadata) : null;

  return (
    <div className="pud-modal-overlay" onClick={onClose}>
      <div
        className="pud-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="pud-modal-header">
          <div className="pud-modal-title">Issue Details</div>
          <button type="button" className="pud-modal-close" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: "center", color: "#6b7280" }}>
            Loading...
          </div>
        )}

        {!loading && issue && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              {issue.image_url && (
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <img
                    src={issue.image_url}
                    alt="Issue"
                    onClick={() => setIssueLightboxOpen(true)}
                    style={{
                      width: 200,
                      height: 160,
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      objectFit: "cover",
                      cursor: "zoom-in",
                    }}
                  />
                  {markup != null && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        minWidth: 22,
                        height: 22,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 6px",
                        borderRadius: 9999,
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {markup}
                    </span>
                  )}
                  <ImageLightbox
                    isOpen={issueLightboxOpen}
                    onClose={() => setIssueLightboxOpen(false)}
                    images={issue.image_url}
                  />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: "#0d0d0e", marginBottom:".5vh"}}>
                  {issue.description || "-"}
                </div>
                {issue.issue_category && (
                  <div
                    style={{ fontSize: 13, color: "#949daa", marginTop: 2 }}
                  >
                    {issue.issue_category.activityType} •{" "}
                    {issue.issue_category.category}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
                fontSize: 13,
              }}
            >
              <div style={{display: "flex", flexDirection : "column", gap: "5px"}}>
                <div style={{ color: "#949daa" }}>Source</div>
                <div style={{ color: "#0d0d0e" }}>
                  {getSourceTypeLabel(issue.source_type)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ color: "#949daa" }}>Status</div>
                <span style={{ ...pillBase, ...getStatusStyles(issue.status) }}>
                  {formatEnumForDisplay(issue.status) || "-"}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px"}}>
                <div style={{ color: "#949daa" }}>Severity</div>
                <span
                  style={{
                    ...pillBase,
                    ...getPriorityStyles(issue.issue_category?.severity),
                  }}
                >
                  {formatEnumForDisplay(issue.issue_category?.severity) ?? "-"}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <div style={{ color: "#949daa" }}>Date Logged</div>
                <div style={{ color: "#0d0d0e" }}>
                  {issue.created_at
                    ? moment(issue.created_at).format("MMM DD, YYYY")
                    : "-"}
                </div>
              </div>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#0d0d0e",
                  marginBottom: 8,
                }}
              >
                Update Status
              </label>
              <CAutocomplete
                placeholder="Select status"
                value={selectedStatus || null}
                onChange={(_e, newValue) => setSelectedStatus(newValue || null)}
                options={STATUS_OPTIONS}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
              />
            </div>

            {isSelectingResolved && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <CInput
                  multiline
                  rows={3}
                  label="Comments *"
                  placeholder="e.g. Fixed the water damage issue"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  disabled={saving}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label style={{ fontSize: 13, color: "#6b7280" }}>
                    Upload image *
                  </label>
                  {resolveFilePreview ? (
                    <div
                      style={{
                        position: "relative",
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                        background: "#f9fafb",
                        display: "inline-block",
                        width: "fit-content",
                      }}
                    >
                      <img
                        src={resolveFilePreview}
                        alt=""
                        style={{
                          width: "auto",
                          maxWidth: "100%",
                          maxHeight: 192,
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                      <button
                        type="button"
                        onClick={removeResolveFile}
                        disabled={saving}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          padding: 4,
                          borderRadius: "50%",
                          background: "#1f2937",
                          color: "#fff",
                          border: "none",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        aria-label="Remove image"
                      >
                        <IoClose size={14} />
                      </button>
                    </div>
                  ) : (
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid #4D6BF6",
                        color: "#4D6BF6",
                        background: "#fff",
                        cursor: "pointer",
                        fontSize: 14,
                        width: "100%",
                      }}
                    >
                      <FiUpload size={18} />
                      Upload file
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleResolveFile}
                        style={{ display: "none" }}
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {isAlreadyResolved && resolvedMeta && (
              <div
                style={{
                  padding: 12,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#0d0d0e",
                    marginBottom: 12,
                  }}
                >
                  Resolved Details
                </div>
                {resolvedMeta.comments && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: "#949daa" }}>
                      Comments
                    </div>
                    <div
                      style={{ fontSize: 13, color: "#0d0d0e", marginTop: 2 }}
                    >
                      {resolvedMeta.comments}
                    </div>
                  </div>
                )}
                {resolvedMeta.imageUrl && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#949daa",
                        marginBottom: 4,
                      }}
                    >
                      Resolved image
                    </div>
                    <img
                      src={resolvedMeta.imageUrl}
                      alt="Resolved"
                      onClick={() => setResolvedLightboxOpen(true)}
                      style={{
                        width: 160,
                        height: 128,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid #e5e7eb",
                        cursor: "zoom-in",
                      }}
                    />
                    <ImageLightbox
                      isOpen={resolvedLightboxOpen}
                      onClose={() => setResolvedLightboxOpen(false)}
                      images={resolvedMeta.imageUrl}
                    />
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginTop: 8,
              }}
            >
              <button
                type="button"
                className="pud-btn"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pud-btn pud-btn-primary"
                onClick={handleSave}
                disabled={saving || !selectedStatus || !resolveValid}
              >
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
